import React from "react";
import { Box, HStack, SimpleGrid, Stack, Text } from "@chakra-ui/react";
import { BarList, ChartCard, DataTable, ShareBar, StatTile, StatusIcon } from "./parts";
import SegmentColumns from "./SegmentColumns";
import { useVizTokens } from "./tokens";
import { FIX_WORDS, count, dateTime, money } from "./format";

// Where texting money is wasted: long texts, the characters that make a text expensive, texts
// that never arrived, and the costliest single texts.

// Twilio's delivery status in words (undelivered and failed show as "No" with their error).
const ARRIVED_WORDS = { delivered: "Yes", read: "Yes", sent: "Sent, no receipt", queued: "Not yet", accepted: "Not yet" };

export default function WastePanel({ report }) {
  const t = useVizTokens();
  const totals = report.totals;
  const encodings = report.encodings || [];
  const plain = totals.plain_characters || {};
  const undeliveredRows = (report.undelivered || []).map((u) => ({
    key: u.code, label: u.label, value: u.cost, valueLabel: money(u.cost),
    sub: `${count(u.texts)} texts${u.code !== "none" ? ` · error ${u.code}` : ""}`,
  }));
  const languageRows = (report.languages || []).filter((l) => l.texts > 0).map((l) => ({
    key: l.key, label: l.label, value: l.cost, valueLabel: money(l.cost),
    sub: `${count(l.texts)} texts · ${l.texts ? (l.segments / l.texts).toFixed(1) : 0} segments each`,
  }));

  return (
    <Stack spacing={4}>
      {report.direction && report.direction !== "outbound" && (
        <HStack align="flex-start" spacing={2} data-testid="sms-waste-sent-only">
          <Box pt="2px"><StatusIcon status="info" size={14} /></Box>
          <Text fontSize="sm" color={t.textSecondary}>
            The savings, the parts and the characters count only the texts the clinics send: patients choose what
            they write. The charts below count every text shown.
          </Text>
        </HStack>
      )}
      <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={3}>
        <StatTile label="Paid for texts that never arrived" value={money(totals.undelivered_cost)}
                  help={`${count(totals.undelivered_texts)} texts`}
                  status={totals.undelivered_cost > 0 ? "critical" : "good"}
                  statusWords={totals.undelivered_cost > 0 ? "Twilio still bills these" : "None"} />
        <StatTile label="Plain characters would save" value={money(plain.cost)}
                  help={`${count(plain.texts)} texts, ${count(plain.segments)} segments fewer`} />
        <StatTile label="Re-sent in parts" value={money(totals.split_cost)}
                  help={`${count(totals.split_texts)} "Part 1/3" texts`} />
        <StatTile label={`Cost above ${totals.target_segments} segments`} value={money(totals.savings_long)}
                  help="what the long texts cost beyond 2 segments each" />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        <ChartCard title="Texts by length" subtitle="Cost of texts of each length, in segments"
                   table={{
                     columns: [
                       { key: "label", label: "Segments" },
                       { key: "texts", label: "Texts", numeric: true, render: (r) => count(r.texts) },
                       { key: "cost", label: "Cost", numeric: true, render: (r) => money(r.cost) },
                     ],
                     rows: report.segment_buckets,
                   }}>
          <SegmentColumns buckets={report.segment_buckets} />
        </ChartCard>
        <ChartCard title="Cheap or expensive format"
                   subtitle="One character outside the plain alphabet makes the whole text expensive"
                   table={{
                     columns: [
                       { key: "label", label: "Format" },
                       { key: "texts", label: "Texts", numeric: true, render: (r) => count(r.texts) },
                       { key: "segments", label: "Segments", numeric: true, render: (r) => count(r.segments) },
                       { key: "cost", label: "Cost", numeric: true, render: (r) => money(r.cost) },
                     ],
                     rows: encodings,
                   }}>
          <Stack spacing={5}>
            <Box>
              <Text fontSize="xs" color={t.textSecondary} mb={2}>Share of texts</Text>
              <ShareBar format={count} segments={encodings.map((e, i) => ({
                key: e.key, label: e.label, value: e.texts, color: i === 0 ? t.series[0] : t.series[1],
              }))} />
            </Box>
            <Box>
              <Text fontSize="xs" color={t.textSecondary} mb={2}>Share of cost</Text>
              <ShareBar format={money} segments={encodings.map((e, i) => ({
                key: e.key, label: e.label, value: e.cost, color: i === 0 ? t.series[0] : t.series[1],
              }))} />
            </Box>
          </Stack>
        </ChartCard>
      </SimpleGrid>

      <ChartCard title="Characters that make texts expensive"
                 subtitle="What the texts that contain each one cost, and where they come from">
        <DataTable
          columns={[
            { key: "label", label: "Character" },
            { key: "texts", label: "Texts", numeric: true, render: (r) => count(r.texts) },
            { key: "cost", label: "Those texts cost", numeric: true, render: (r) => money(r.cost) },
            { key: "sources", label: "Mostly in", wrap: true, render: (r) => (r.sources || []).join(", ") || "-" },
            { key: "fix", label: "How to fix", wrap: true, render: (r) => FIX_WORDS[r.fix] || "-" },
          ]}
          rows={report.characters || []}
        />
      </ChartCard>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        <ChartCard title="Texts that never arrived" subtitle="Twilio bills a text the carrier refused"
                   table={{
                     columns: [
                       { key: "label", label: "Why" },
                       { key: "code", label: "Error" },
                       { key: "texts", label: "Texts", numeric: true, render: (r) => count(r.texts) },
                       { key: "cost", label: "Cost", numeric: true, render: (r) => money(r.cost) },
                     ],
                     rows: report.undelivered || [],
                   }}>
          <BarList rows={undeliveredRows} empty="Every text in this range arrived." />
        </ChartCard>
        <ChartCard title="Language of the texts" subtitle="By most of a text's letters"
                   table={{
                     columns: [
                       { key: "label", label: "Language" },
                       { key: "texts", label: "Texts", numeric: true, render: (r) => count(r.texts) },
                       { key: "cost", label: "Cost", numeric: true, render: (r) => money(r.cost) },
                     ],
                     rows: report.languages || [],
                   }}>
          <BarList rows={languageRows} />
        </ChartCard>
      </SimpleGrid>

      <ChartCard title="Most expensive texts" subtitle="The costliest single texts in this range">
        <DataTable
          columns={[
            { key: "sent_at", label: "Sent", render: (r) => dateTime(r.sent_at) },
            { key: "patient", label: "Patient", render: (r) => `${r.patient} (${r.phone})` },
            { key: "source_label", label: "Why" },
            { key: "segments", label: "Segments", numeric: true },
            { key: "cost", label: "Cost", numeric: true, render: (r) => money(r.cost) },
            { key: "status", label: "Arrived", render: (r) => (["undelivered", "failed"].includes(r.status) ? (
              <HStack spacing={1}><StatusIcon status="critical" size={14} /><Text as="span">No{r.error_code ? ` (${r.error_code})` : ""}</Text></HStack>
            ) : ARRIVED_WORDS[r.status] || r.status || "-") },
            { key: "preview", label: "Text", wrap: true, render: (r) => (
              <Box>
                <Text fontSize="xs" color={t.text} noOfLines={2}>{r.preview}</Text>
                {r.characters?.length > 0 && (
                  <Text fontSize="xs" color={t.muted}>Expensive because of: {r.characters.join(", ")}</Text>
                )}
              </Box>
            ) },
          ]}
          rows={(report.expensive || []).map((e) => ({ ...e, key: e.sid }))}
        />
      </ChartCard>
    </Stack>
  );
}
